import { HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { STORAGE_KEYS } from './api.config';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { catchError, throwError } from 'rxjs';
import { CONTENT_SYNC_UNAVAILABLE, CREATOR_REQUIRED } from './content.service';

/** Parses JWT payload and returns exp (seconds since epoch) or null if invalid. */
function parseJwtExp(token: string): number | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
    const json = atob(padded);
    const payload = JSON.parse(json) as { exp?: number };
    return typeof payload.exp === 'number' ? payload.exp : null;
  } catch {
    return null;
  }
}

/** Returns true if token is expired (exp in past) or unparseable. */
function isTokenExpired(token: string): boolean {
  const exp = parseJwtExp(token);
  if (exp === null) return true;
  return exp * 1000 < Date.now();
}

/** 403 codes the content pages explain themselves (or hide on), so no generic toast. */
const QUIET_403_CODES: string[] = [CONTENT_SYNC_UNAVAILABLE, CREATOR_REQUIRED];

/**
 * Content sync answers 403 to orgs without it (B1) and to people who may not manage it (Players).
 * Those reads only decide whether the Content area and the Team nav show, so they stay quiet.
 */
function isQuietForbidden(req: HttpRequest<unknown>, err: { error?: { code?: unknown } }): boolean {
  const code = err?.error?.code;
  if (typeof code === 'string' && QUIET_403_CODES.includes(code)) return true;
  return req.method === 'GET' && req.url.includes('/api/v1/content/');
}

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const router = inject(Router);
  const messages = inject(MessageService);
  const token = localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN) || sessionStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
  const isApi = req.url.includes('/api/');
  const authReq = token && isApi ? req.clone({ setHeaders: { Authorization: `Bearer ${token}` } }) : req;
  return next(authReq).pipe(
    catchError((err) => {
      if (!err) return throwError(() => err);

      if (err.status === 401) {
        const expired = !token || isTokenExpired(token);
        if (expired) {
          localStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN);
          sessionStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN);
          try { messages.add({ severity: 'warn', summary: 'Session expired', detail: 'Please sign in again.' }); } catch {}
          router.navigate(['/signin']);
        } else {
          try {
            messages.add({
              severity: 'error',
              summary: 'Request failed',
              detail: err.error?.message || 'Something went wrong. Please try again.',
            });
          } catch {}
        }
      } else if (err.status === 403 && !isQuietForbidden(req, err)) {
        try {
          messages.add({
            severity: 'warn',
            summary: 'Access denied',
            detail: err.error?.message || 'You do not have permission for this action.',
          });
        } catch {}
      }

      return throwError(() => err);
    })
  );
};


